import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface DateTimePickerComponentProps {
  label?: string;
  value?: Date | number | null;
  onChange?: (newValue: number | null) => void;
  className?: string;
  onInputClicked?: () => void;
  onBlurred?: () => void;
  autoSelectDateOption?: "today" | "tomorrow" | null;
  skipMinDate?: boolean;
  minDate?: number;
  maxDate?: number;
}

const DateTimePickerComponent = forwardRef<
  { reset: () => void },
  DateTimePickerComponentProps
>(
  (
    {
      label = "Select Date and Time",
      value,
      onChange,
      className,
      onInputClicked,
      onBlurred,
      autoSelectDateOption = null,
      skipMinDate = false,
      minDate,
      maxDate,
    },
    ref
  ) => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(
      value ? (typeof value === "number" ? new Date(value) : value) : null
    );

    useEffect(() => {
      if (autoSelectDateOption && !value) {
        // console.log(autoSelectDateOption, value);
        const currentDate = new Date();
        if (autoSelectDateOption === "today") {
          setSelectedDate(currentDate); // Set to today
          if (onChange) onChange(currentDate.getTime());
        } else if (autoSelectDateOption === "tomorrow") {
          const tomorrow = new Date(currentDate);
          tomorrow.setDate(currentDate.getDate() + 1); // Increment date by 1
          setSelectedDate(tomorrow); // Set to tomorrow
          if (onChange) onChange(tomorrow.getTime());
        }
      } else if (value) {
        setSelectedDate(typeof value === "number" ? new Date(value) : value); // Set to provided value or convert timestamp to Date
      }
    }, [autoSelectDateOption, value]);

    const currentDate = new Date();

    // Expose reset function to parent
    useImperativeHandle(ref, () => ({
      reset() {
        if (autoSelectDateOption === "today") {
          const today = new Date();
          setSelectedDate(today);
          if (onChange) onChange(today.getTime());
        } else if (autoSelectDateOption === "tomorrow") {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          setSelectedDate(tomorrow);
          if (onChange) onChange(tomorrow.getTime());
        } else {
          setSelectedDate(
            typeof value === "number" ? new Date(value) : value || null
          );
        }
      },
    }));

    const handleDateChange = (newValue: Date | null) => {
      setTimeout(() => {
        setSelectedDate(newValue);
        if (onChange) onChange(newValue ? newValue.getTime() : null);
      }, 150);
    };

    const handleInputBlur = () => {
      setTimeout(() => {
        if (onBlurred) onBlurred();
      }, 150);
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DateTimePicker
          onClose={handleInputBlur}
          minDate={
            skipMinDate ? undefined : minDate ? new Date(minDate) : currentDate
          } // Conditionally set minDate
          maxDate={maxDate ? new Date(maxDate) : undefined} // Conditionally set maxDate
          format="dd/MMM/yyyy hh:mm aa"
          className={className}
          label={label}
          value={selectedDate}
          onChange={handleDateChange}
          slotProps={{
            toolbar: {
              hidden: true,
            },
            textField: {
              InputProps: {
                placeholder: `${label}`,
              },
              role: "button",
              onClick: onInputClicked,
              onBlur: handleInputBlur,
            },
          }}
        />
      </LocalizationProvider>
    );
  }
);

export default DateTimePickerComponent;
